import jwtDecode from "jwt-decode";

import { Permissions, Groups, AccessToken, rcmGroups } from "src/auth/types/userToken";

import { getTokenForCurrentUser, hasCognitoToken } from "./userToken";

export enum Action {
  CREATE_QUEUE,
  UPDATE_QUEUE,
  CREATE_PROCESSING_RULE,
  UPDATE_PROCESSING_RULE,
  APPROVE_PROCESSING_RULE,
  DELETE_PROCESSING_RULE,
  REORDER_PROCESSING_RULES,
  COPY_RULES,
  SEARCH,
  CAN_PERFORM_QUEUE_ACTION,
  SETTINGS,
  VIEW_CATCH_ALL,
  VIEW_DEFERRALS,
  VIEW_ESCALATIONS,
  VIEW_SETTINGS_RULES,
  VIEW_OCC_HEALTH_ORGANIZATIONS,
  OCC_ADD_PAYMENT_DETAILS,
  EDIT_BILL,
  VIEW_PAYMENT_TEAM,
  VIEW_SETTINGS_TIMELY_FILING_LIMITS,
  VIEW_SETTINGS_CPT_MAX_UNITS,
  MANAGE_ESCALATION_DEFERRAL_QUEUES,
  VIEW_SETTINGS_PROVIDER_DX_SUGGESTIONS,
}

interface TokenData {
  permissions: string[];
  groups: string[];
}

// Predicate functions for group checks
const canEditBills = (groups: string[]) =>
  isMemberOfAnyGroup(groups, [
    Groups.RCM_ADMINS,
    Groups.RCM_PRACTICE_ADMINS,
    Groups.RCM_MEMBERS,
    Groups.RCM_FRONT_DESK_STAFF,
  ]);

const isRcmUser = (groups: string[]) =>
  isMemberOfAnyGroup(groups, [Groups.RCM_ADMINS, Groups.RCM_PRACTICE_ADMINS, Groups.RCM_MEMBERS]);

const isRcmOrOccUser = (groups: string[]) =>
  isRcmUser(groups) || groups.includes(Groups.RCM_OCC_HEALTH);

const isRcmAdmin = (groups: string[]) => groups.includes(Groups.RCM_ADMINS);

const isRcmPracticeAdmin = (groups: string[]) => groups.includes(Groups.RCM_PRACTICE_ADMINS);

const canViewPaymentTeam = (groups: string[]) =>
  isMemberOfAnyGroup(groups, [
    Groups.RCM_PAYMENTS,
    Groups.RCM_DME_BILLING,
    Groups.RCM_VBC_BILLING,
  ]) || isRcmOrOccUser(groups);

const canSearchBills = (groups: string[]) =>
  isRcmUser(groups) || groups.includes(Groups.RCM_PRACTICE_STAFF);

const actionRequirements: Record<Action, (data: TokenData) => boolean> = {
  // View actions - available to all RCM users except OCC/front desk/practice staff
  [Action.VIEW_SETTINGS_RULES]: ({ groups }) => isRcmUser(groups),
  [Action.CAN_PERFORM_QUEUE_ACTION]: ({ groups }) => isRcmUser(groups),
  [Action.VIEW_CATCH_ALL]: ({ groups }) => isRcmUser(groups),
  [Action.VIEW_DEFERRALS]: ({ groups }) => isRcmUser(groups),
  [Action.VIEW_ESCALATIONS]: ({ groups }) => isRcmUser(groups),

  // Search actions - available to RCM and Practice Staff users
  [Action.SEARCH]: ({ groups }) => canSearchBills(groups),

  // Settings - available to all RCM and OCC users
  [Action.SETTINGS]: ({ groups }) => isRcmOrOccUser(groups),
  [Action.VIEW_SETTINGS_TIMELY_FILING_LIMITS]: ({ groups }) =>
    isRcmAdmin(groups) || isRcmPracticeAdmin(groups),
  [Action.VIEW_SETTINGS_CPT_MAX_UNITS]: ({ groups }) =>
    isRcmAdmin(groups) || isRcmPracticeAdmin(groups),
  [Action.VIEW_SETTINGS_PROVIDER_DX_SUGGESTIONS]: ({ groups }) =>
    isRcmAdmin(groups) || isRcmPracticeAdmin(groups),

  // Queue management - permission based
  [Action.CREATE_QUEUE]: ({ permissions }) => permissions.includes(Permissions.CREATE_QUEUE),
  [Action.UPDATE_QUEUE]: ({ permissions }) => permissions.includes(Permissions.UPDATE_QUEUE),

  // Admin only actions
  [Action.CREATE_PROCESSING_RULE]: ({ groups }) => isRcmAdmin(groups),
  [Action.UPDATE_PROCESSING_RULE]: ({ groups }) => isRcmAdmin(groups),
  [Action.APPROVE_PROCESSING_RULE]: ({ groups }) => isRcmAdmin(groups),
  [Action.DELETE_PROCESSING_RULE]: ({ groups }) => isRcmAdmin(groups),
  [Action.REORDER_PROCESSING_RULES]: ({ groups }) => isRcmAdmin(groups),
  [Action.COPY_RULES]: ({ groups }) => isRcmAdmin(groups),

  // Edit bill - excludes practice staff
  [Action.EDIT_BILL]: ({ groups }) => canEditBills(groups),

  // Payment workflow visibility - RCM_PAYMENTS or RCM_DME_BILLING or RCM_VBC_BILLING or RCM/OCC users
  [Action.VIEW_PAYMENT_TEAM]: ({ groups }) => canViewPaymentTeam(groups),
  [Action.VIEW_OCC_HEALTH_ORGANIZATIONS]: ({ groups }) => isRcmOrOccUser(groups),
  [Action.OCC_ADD_PAYMENT_DETAILS]: ({ groups }) => isRcmOrOccUser(groups),

  // Escalation and Deferral queue management - RCM users only
  [Action.MANAGE_ESCALATION_DEFERRAL_QUEUES]: ({ groups }) => isRcmUser(groups),
};

function parseCognitoToken(token: string): TokenData {
  const decodedToken = jwtDecode<AccessToken>(token);
  return {
    permissions: decodedToken?.permissions?.split(",") ?? [],
    groups: decodedToken?.groups?.split(",") ?? [],
  };
}

function getTokenPermissionsAndGroups(): TokenData {
  const token = getTokenForCurrentUser();

  if (!token) {
    return { permissions: [], groups: [] };
  }

  if (hasCognitoToken()) {
    return parseCognitoToken(token);
  }

  return { permissions: [], groups: [] };
}

export function isUserMemberOfAnyGroup(groupsToCheck: string[]): boolean {
  const { groups } = getTokenPermissionsAndGroups();
  return groups.some((group) => groupsToCheck.includes(group));
}

export function isUserOnlyMemberOfGroups(groupsToCheck: string[]): boolean {
  const { groups } = getTokenPermissionsAndGroups();
  const excludedGroups = rcmGroups
    .filter((group) => !groupsToCheck.includes(group.toString()))
    .map((group) => group.toString());
  return (
    excludedGroups.every((excludedGroup) => !groups.includes(excludedGroup)) &&
    groupsToCheck.every((includedGroup) => groups.includes(includedGroup))
  );
}

export function isUserMemberOfGroup(groupToCheck: string): boolean {
  const { groups } = getTokenPermissionsAndGroups();
  return groups.includes(groupToCheck);
}

function isMemberOfAnyGroup(userGroups: string[], groupsToCheck: string[]): boolean {
  return userGroups.some((group) => groupsToCheck.includes(group));
}

export function canTakeAction(action: Action): boolean {
  const tokenData = getTokenPermissionsAndGroups();
  const requirement = actionRequirements[action];

  return requirement ? requirement(tokenData) : false;
}

export function hasPermission(permission: Permissions): boolean {
  const { permissions } = getTokenPermissionsAndGroups();
  return permissions.includes(permission);
}
